import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import '../css/Calculator.css';
import Lottie from 'lottie-react';
import languageIconAnimation from  '../assets/language.json';
import translations from "../constants/translations";
import '../css/LanguageModal.css';

const Calculator = () => {
    const [inputFields, setInputFields] = useState({
        name: '',
        commute: '0',
        women: '0',
        lpgCylinders: '0',
        electricity: '0',
        healthcare: '0',
        employment: '0',
        debt: '0'
    });
    const [isFormValid, setIsFormValid] = useState(false);
    const [language, setLanguage] = useState(localStorage.getItem('language') || null); // Set to null if no language found
    const [showLanguageModal, setShowLanguageModal] = useState(!localStorage.getItem('language')); // Show modal if no language in storage
    const [translatedText, setTranslatedText] = useState(language ? translations[language] : translations['marathi']);

    const navigate = useNavigate();


    const handleLanguageSelect = (lang) => {
        setLanguage(lang);
        localStorage.setItem('language', lang);
        setTranslatedText(translations[lang])
        setShowLanguageModal(false);
    };


    // Function to check if all input fields are filled and valid
    const allFieldsFilled = () => {
        return inputFields.name &&
            isWholeNumber(inputFields.commute) &&
            isWholeNumber(inputFields.women) &&
            isWholeNumber(inputFields.lpgCylinders) &&
            isWholeNumber(inputFields.electricity) &&
            isWholeNumber(inputFields.healthcare) &&
            isWholeNumber(inputFields.employment) &&
            isWholeNumber(inputFields.debt) &&
            getWholeNumber(inputFields.electricity) > 0;
    };

    // Helper function to convert Devanagari numerals to Arabic numerals
    const convertDevanagariToArabic = (input) => {
        const devanagariToArabicMap = {
            '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
            '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
        };
        return input.replace(/[०१२३४५६७८९]/g, match => devanagariToArabicMap[match]);
    };

    // Function to check if the input is a whole number and >= 0
    const isWholeNumber = (value) => {
        // Convert Devanagari numerals to Arabic numerals
        value = convertDevanagariToArabic(value);
        const number = Number(value);
        return Number.isInteger(number) && number >= 0;
    };

    const getWholeNumber = (value) => {
        // Convert Devanagari numerals to Arabic numerals
        value = convertDevanagariToArabic(value);
        const number = Number(value);
        return number;
    };

    // Validate number fields when inputs change
    const handleChange = (e) => {
        setInputFields({
            ...inputFields,
            [e.target.name]: e.target.value,
        });
    };

    const handleInputChange = (e) => {
        let value = e.target.value;

        // Convert Devanagari to Arabic numerals
        value = convertDevanagariToArabic(value);

        // Only allow numeric characters
        if (/^[0-9]*$/.test(value)) {
            setInputFields({
                ...inputFields,
                [e.target.name]: e.target.value,
            });
        }
    };

    // Validate form fields after any input change
    useEffect(() => {
        setIsFormValid(allFieldsFilled());
    }, [inputFields]);

    // Submit logic to calculate the result
    const handleSubmit = (e) => {
        e.preventDefault();

        const numWomen = getWholeNumber(inputFields.women);
        const numLPG = getWholeNumber(inputFields.lpgCylinders);
        const commute = getWholeNumber(inputFields.commute);
        const healthcare = getWholeNumber(inputFields.healthcare);
        const electricity = getWholeNumber(inputFields.electricity);
        const debt = getWholeNumber(inputFields.debt);
        const employment = getWholeNumber(inputFields.employment);

        const womenExpense = numWomen * 3000;
        const lpgExpense = numLPG * 302.5 * Math.min(numLPG, 6);
        const employmentExpense = employment * 4000;
        const debtExpense = Math.min(25000, debt);

        // Electricity bill calculation
        let remainingBill = 0;


        if (electricity > 0) {
            if (electricity <= 588) {
                remainingBill = electricity;
            } else if (electricity <= 2880) {
                const unitsConsumed = 100 + Math.floor((electricity - 588) / 11.46);
                let newUnits = unitsConsumed - 100;
                if (newUnits <= 100) {
                    remainingBill = newUnits * 5.88;
                } else {
                    remainingBill = (588 + newUnits - 100) * 11.46;
                }
            } else {
                remainingBill = 0;
            }
        }

        const monthlySavings = womenExpense + lpgExpense + commute + healthcare + employmentExpense + debtExpense + remainingBill;

        // Store results and navigate to result page
        localStorage.setItem('total', monthlySavings.toFixed(2));
        localStorage.setItem('name', inputFields.name);

        navigate('/result');
    };

    return (
        <div id="SITE_CONTAINER">

            {/* Language Selection Modal */}
            {showLanguageModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <button className="modal-close-button" onClick={() => setShowLanguageModal(false)}>✖</button>
                        <h2>Select Language</h2>
                        <div className="language-option" onClick={() => handleLanguageSelect('english')}>English</div>
                        <div className="language-option" onClick={() => handleLanguageSelect('hindi')}>हिंदी</div>
                        <div className="language-option" onClick={() => handleLanguageSelect('marathi')}>मराठी</div>
                    </div>
                </div>
            )}

            <div className="calculator-form-container">

                <div className="banner">
                    <img
                        src="https://images.netaapp.in/2024-11-317/22/a6952610-e956-4ee8-9ee1-3c847732271a"
                        alt="Banner"
                        className="banner-image"
                    />
                    {/* Wrap Lottie in a div with the onClick handler */}
                    <div className="lottie-overlay" onClick={() => setShowLanguageModal(true)}>
                        <Lottie animationData={languageIconAnimation} loop={true} style={{ height: 70, width: 70 }} />
                    </div>
                </div>

                <form className="calculator-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{translatedText.nameLabel}</label>
                        <input
                            type="text"
                            name="name"
                            value={inputFields.name}
                            onChange={handleChange}
                            placeholder={translatedText.namePlaceHolder}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.womenLabel}</label>
                        <input
                            type="text"
                            name="women"
                            value={inputFields.women}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.commuteLabel}</label>
                        <input
                            type="text"
                            name="commute"
                            value={inputFields.commute}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.lpgCylindersLabel}</label>
                        <input
                            type="text"
                            name="lpgCylinders"
                            value={inputFields.lpgCylinders}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.healthcareLabel}</label>
                        <input
                            type="text"
                            name="healthcare"
                            value={inputFields.healthcare}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.employmentLabel}</label>
                        <input
                            type="text"
                            name="employment"
                            value={inputFields.employment}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.debtLabel}</label>
                        <input
                            type="text"
                            name="debt"
                            value={inputFields.debt}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{translatedText.electricityLabel}</label>
                        <input
                            type="text"
                            name="electricity"
                            value={inputFields.electricity}
                            onChange={handleInputChange}
                            placeholder="0"
                            required
                        />
                    </div>
                    {/*/!* Error message rendered conditionally *!/*/}
                    {!isFormValid && (
                        <p style={{color: 'red', fontWeight: 'bold'}}>
                            {translatedText.errorLabel}
                        </p>
                    )}
                    <button type="submit" disabled={!isFormValid}>{translatedText.submitButton}</button>
                </form>
            </div>
        </div>
    );
};

export default Calculator;
